/**
 * Notification Onboarding Component
 * 
 * Guides users through enabling notifications at optimal moments
 * Shows after first check-in or during onboarding
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from '../lib/motion-utils';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Bell, X, Clock, TrendingUp, Zap, CheckCircle2 } from 'lucide-react';
import {
  requestNotificationPermission,
  areNotificationsEnabled,
  sendTestNotification,
  updateNotificationSettings,
} from '../lib/notification-service';
import { toast } from 'sonner';
import { NotificationPreview } from './NotificationPreview';

interface NotificationOnboardingProps {
  trigger?: 'first-checkin' | 'onboarding' | 'manual';
  onComplete?: () => void;
  onSkip?: () => void;
}

export function NotificationOnboarding({ 
  trigger = 'manual',
  onComplete,
  onSkip 
}: NotificationOnboardingProps) {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedTime, setSelectedTime] = useState('20:00');

  useEffect(() => {
    // Don't show if already enabled
    if (areNotificationsEnabled()) {
      return;
    }

    // Check if user has dismissed before
    const dismissed = localStorage.getItem('tension_notification_onboarding_dismissed');
    if (dismissed) {
      return;
    }

    // Show based on trigger
    if (trigger === 'first-checkin') {
      // Show after first check-in
      const checkins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
      if (checkins.length === 1) {
        setTimeout(() => setShow(true), 2000);
      }
    } else if (trigger === 'onboarding') {
      // Show immediately
      setShow(true);
    } else if (trigger === 'manual') {
      setShow(true);
    }
  }, [trigger]);

  const handleEnable = async () => {
    const granted = await requestNotificationPermission();

    if (granted) {
      // Update reminder time
      updateNotificationSettings({
        reminderTime: selectedTime,
      });
      
      toast.success('Notifications enabled!');
      setTimeout(() => sendTestNotification(), 500);
      
      setStep(3);
      setTimeout(() => {
        setShow(false);
        onComplete?.();
      }, 2000);
    } else {
      toast.error('Notification permission denied. You can enable this later in Settings.');
      handleSkip();
    }
  };

  const handleSkip = () => {
    localStorage.setItem('tension_notification_onboarding_dismissed', 'true');
    setShow(false);
    onSkip?.();
  };

  const benefits = [
    {
      icon: Clock,
      title: 'Daily Consistency',
      description: 'Never miss a check-in. Build the habit that makes tension tracking work.',
    },
    {
      icon: TrendingUp,
      title: 'Better Forecasting',
      description: 'Consistent data means more accurate predictions of tension patterns.',
    },
    {
      icon: Zap,
      title: 'Timely Interventions',
      description: 'Get nudged with micro-actions exactly when they\'ll have the most impact.',
    },
  ];

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={handleSkip}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="pointer-events-auto w-full max-w-lg"
            >
              <Card className="p-8 relative">
                {step < 3 && (
                  <button
                    onClick={handleSkip}
                    className="absolute top-4 right-4 p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-neutral-400" />
                  </button>
                )}

                {/* Step 1: Introduction */}
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto">
                      <Bell className="w-8 h-8 text-blue-600" />
                    </div>

                    <div className="text-center space-y-2">
                      <h2 className="text-2xl font-light">Stay Consistent with Reminders</h2>
                      <p className="text-neutral-600">
                        Daily check-ins are the foundation of TENSION. Gentle reminders help you build the habit.
                      </p>
                    </div>

                    <div className="space-y-4">
                      {benefits.map((benefit, index) => (
                        <div key={index} className="flex gap-4 items-start">
                          <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <benefit.icon className="w-5 h-5 text-neutral-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-sm">{benefit.title}</h3>
                            <p className="text-sm text-neutral-600">{benefit.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Preview */}
                    <div className="pt-4">
                      <p className="text-xs text-neutral-600 mb-3">Example notification:</p>
                      <NotificationPreview type="checkin" />
                    </div>

                    <Button
                      onClick={() => setStep(2)}
                      className="w-full"
                      size="lg"
                    >
                      Set Up Notifications
                    </Button>

                    <button
                      onClick={handleSkip}
                      className="w-full text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                    >
                      Maybe later
                    </button>
                  </motion.div>
                )}

                {/* Step 2: Choose Time */}
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto">
                      <Clock className="w-8 h-8 text-blue-600" />
                    </div>

                    <div className="text-center space-y-2">
                      <h2 className="text-2xl font-light">When should we remind you?</h2>
                      <p className="text-neutral-600">
                        Pick a time when you typically wind down and reflect. We'll learn your patterns and adjust.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-neutral-700">Daily Reminder Time</label>
                        <input
                          type="time"
                          value={selectedTime}
                          onChange={(e) => setSelectedTime(e.target.value)}
                          className="w-full px-4 py-3 text-lg border-2 border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-900">
                          💡 <strong>Adaptive Learning:</strong> TENSION will automatically learn your optimal check-in time based on when you actually complete check-ins.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Button
                        onClick={handleEnable}
                        className="w-full"
                        size="lg"
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Enable Notifications
                      </Button>

                      <button
                        onClick={() => setStep(1)}
                        className="w-full text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                      >
                        Back
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Success */}
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-6 text-center py-8"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring' }}
                      className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto"
                    >
                      <CheckCircle2 className="w-10 h-10 text-green-600" />
                    </motion.div>

                    <div className="space-y-2">
                      <h2 className="text-2xl font-light">You're All Set!</h2>
                      <p className="text-neutral-600">
                        We'll send you a gentle reminder at {selectedTime} each day.
                      </p>
                    </div>

                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs text-green-900">
                        🔒 <strong>Privacy First:</strong> All notifications are sent locally from your device. Your data never leaves your phone.
                      </p>
                    </div>
                  </motion.div>
                )}
              </Card>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}